'use client';

import {
  preprocessListMarkup,
  stripAnchorOnlyList,
} from '@lib/util/typography';
import { MarkdownErrorBoundary } from '@modules/common/components/markdown-error-boundary';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkHeadingID from 'remark-heading-id';
import {
  headingComponentsNatural,
  headingComponentsShifted,
  markdownElementComponents,
  markdownProseClassName,
} from './shared';

type MarkdownContentProps = {
  content: string;
  className?: string;
  /** Inherit text color from parent instead of using prose-p:text-ui-fg-subtle */
  inheritTextColor?: boolean;
  /** Use a compact variant with smaller text and spacing */
  compact?: boolean;
  /** Strip ordered-list TOC blocks (anchor-only lists) before rendering — PDP only */
  stripToc?: boolean;
  /**
   * When true, headings render at their natural level (h2→h2, h3→h3).
   * Use for section content that already has a proper heading hierarchy
   * (e.g. embedded ## headings in variant sections).
   * h1 in markdown still maps to h2 to avoid multiple h1s on the page.
   */
  noHeadingShift?: boolean;
};

/**
 * Renders Markdown content with Tailwind Typography (prose) styling.
 * Handles headings, lists, tables, links, bold/italic, and GFM extensions.
 */
const MarkdownContent = ({
  content,
  className,
  inheritTextColor = false,
  compact = false,
  stripToc = false,
  noHeadingShift = false,
}: MarkdownContentProps) => {
  if (!content || content.trim().length === 0) {
    return null;
  }

  const headingComponents = noHeadingShift
    ? headingComponentsNatural
    : headingComponentsShifted;

  return (
    <MarkdownErrorBoundary>
      <div
        className={markdownProseClassName({
          compact,
          inheritTextColor,
          className,
        })}
        data-testid="markdown-content"
        translate="no"
      >
        <Markdown
          remarkPlugins={[remarkGfm, remarkHeadingID]}
          urlTransform={(url) =>
            /^(https?:|mailto:|tel:|\/(?!\/)|#)/.test(url) ? url : ''
          }
          components={{
            ...headingComponents,
            ...markdownElementComponents,
          }}
        >
          {preprocessListMarkup(
            stripToc ? stripAnchorOnlyList(content) : content,
          )}
        </Markdown>
      </div>
    </MarkdownErrorBoundary>
  );
};

export default MarkdownContent;
